import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { ArrowLeft } from "lucide-react";
import { format } from "date-fns";
import BlockRenderer from "@/components/news/BlockRenderer";
import { pushEvent } from "@/lib/gtm";

export default function NewsArticle() {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined);

  useEffect(() => {
    window.scrollTo(0, 0);
    base44.entities.NewsPost.filter({ slug, status: "published" })
      .then((list) => {
        const p = list[0] || null;
        setPost(p);
        if (!p) return;
        pushEvent("page_view_spa", {
          page_type: "news_article",
          page_path: `/news/${p.slug}`,
          page_title: p.seo_title || p.title,
          article_title: p.title,
          category: p.category || "none",
        });
        document.title = p.seo_title || p.title;
        const desc = p.meta_description || p.excerpt || "";
        const setMeta = (attr, key, content) => {
          if (!content) return;
          let el = document.head.querySelector(`meta[${attr}="${key}"]`);
          if (!el) {
            el = document.createElement("meta");
            el.setAttribute(attr, key);
            document.head.appendChild(el);
          }
          el.setAttribute("content", content);
        };
        setMeta("name", "description", desc);
        setMeta("property", "og:title", p.seo_title || p.title);
        setMeta("property", "og:description", desc);
        setMeta("property", "og:image", p.featured_image);
        let canonical = document.head.querySelector('link[rel="canonical"]');
        if (!canonical) {
          canonical = document.createElement("link");
          canonical.setAttribute("rel", "canonical");
          document.head.appendChild(canonical);
        }
        canonical.setAttribute("href", `https://seisfra.com/news/${p.slug}`);
      })
      .catch(() => setPost(null));
  }, [slug]);

  if (post === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-gray-200 border-t-[#E4001C] rounded-full animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-6 text-center">
        <h1 className="font-heading font-extrabold uppercase text-4xl">Article Not Found</h1>
        <Link to="/news" className="inline-flex items-center gap-2 bg-[#E4001C] text-white font-tech font-semibold uppercase tracking-widest px-6 py-3">
          <ArrowLeft className="w-4 h-4" /> Back to News
        </Link>
      </div>
    );
  }

  return (
    <>
      <section className="bg-[#0A0A0A] pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Link to="/news" className="inline-flex items-center gap-2 font-tech uppercase tracking-widest text-sm text-[#A8ADB4] hover:text-white transition-colors mb-6">
            <ArrowLeft className="w-4 h-4" /> All News
          </Link>
          <div className="flex items-center gap-4 text-sm font-tech uppercase tracking-[0.2em] mb-3">
            {post.category && <span className="text-[#FF4D5E]">{post.category}</span>}
            {post.published_date && <span className="text-[#A8ADB4]">{format(new Date(post.published_date), "MMMM d, yyyy")}</span>}
          </div>
          <h1 className="font-heading font-extrabold uppercase text-white text-4xl md:text-6xl tracking-wide">{post.title}</h1>
          {post.author && <p className="text-[#A8ADB4] font-tech uppercase tracking-widest text-sm mt-4">By {post.author}</p>}
        </div>
      </section>

      <article className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          {post.featured_image && (
            <img src={post.featured_image} alt={post.featured_image_alt || post.title} className="w-full aspect-[16/9] object-cover mb-10" />
          )}
          <BlockRenderer blocks={post.content || []} />
        </div>
      </article>
    </>
  );
}